import Vue from "vue";
import App from "@/App.vue";
import ConfigurationOptions from "@/features/Configuration/ConfigurationOptions";
import router from "@/router";
import vuetify from "@/plugins/vuetify";
import { createPinia, PiniaVuePlugin } from "pinia";
import { SessionService } from "@/features/Authentication/SessionService";
import { ScoreService } from "@/features/Scores/ScoreService";
import { ActionService } from "@/features/Actions/ActionService";
import { IncidentService } from "@/features/Incidents/IncidentService";
import { CommentService } from "@/features/Comments/CommentService";
import { TimelineService } from "@/features/Timeline/TimelineService";
import { DocumentUploadService } from "@/features/Documents/DocumentUploadService";
import { DocumentDownloadService } from "@/features/Documents/DocumentDownloadService";
import { PlatformDocumentUploadService } from "@/features/PlatformDocuments/PlatformDocumentUploadService";
import { NoteService } from "@/features/Notes/NoteService";
import { ReadStateService } from "@/features/SupplierEvents/ReadStateService";
import { SupplierEventService } from "@/features/SupplierEvents/SupplierEventService";
import { SupplierProductService } from "@/features/SupplierEvents/SupplierProductService";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useUserStore } from "@/plugins/pinia";
import { PlatformUserInfoService, UserType } from "@/features/Authentication/PlatformUserInfoService";
import { SupplierService } from "@/features/Supplier/SupplierService";
import { ScoreAuthorizationService } from "@/features/Scores/ScoreAuthorizationService";
import { IncidentAuthorizationService } from "@/features/Incidents/IncidentAuthorizationService";
import { ActionAuthorizationService } from "@/features/Actions/ActionAuthorizationService";
import { SupplierEventAuthorizationService } from "@/features/SupplierEvents/SupplierEventAuthorizationService";

Vue.config.productionTip = false;

// Wrapping startup in a method
// so we can make the async startup process easier to follow
(async () => {
    // Pinia is first, all services use it for global state management
    // See https://pinia.vuejs.org/getting-started.html when moving to Vue 3
    Vue.use(PiniaVuePlugin);
    const pinia = createPinia();

    const response = await axios.get("/api/v1/configuration");
    Vue.prototype.$configuration = response.data as ConfigurationOptions;
    console.log("The configuration is: " + Vue.prototype.$configuration);

    // Service Layer Startup

    // This is performed before the root Vue component is mounted so that the services are ready to go
    // FUTURE: This is starting to get lengthy.
    // We could use a splash screen or something

    // The session service comes early, because it establishes the user
    // and we force users to be logged in to access the app
    // Future: We could show some sort of UI here while we wait
    console.log("Session Service Starting");
    const sessionService = new SessionService(pinia);
    Vue.prototype.$sessionService = sessionService;
    await sessionService.tryInitializeUserSession();
    // If a user isn't logged in, we are letting them past here for now
    // A route filter will handle taking them to a login workflow

    // Need to append the auth token to all outgoing ajax requests
    // This requires the session service to get the auth token
    // and must be on an interceptor to get new access tokens when they expire
    // Before the request is sent off, try to add the auth token
    axios.interceptors.request.use(
        async config => {
            try {
                const userStore = useUserStore(pinia);
                if (!userStore.loggedIn) {
                    // How did we even get here?...
                    // Assuming the get user info request in the session service runs before this is attached
                    // Just go to the logout flow
                    console.log("App attempted to make an API call without being logged in. Redirecting to logout flow.");
                    await sessionService.logout();
                } else {
                    const authToken = await sessionService.getToken();
                    if (!config.headers) {
                        config.headers = {};
                    }
                    config.headers.Authorization = `Bearer ${authToken}`;
                }
            } catch {
                console.log("HTTP: Could not get access token for request.");
            }
            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );

    // If we get a 401 out of that http instance,
    // that means something about the session is invalid.
    // The session service needs to know.
    axios.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError) => {
            if (error.response && error.response.status === 401) {
                // Something must be wrong with the user session.
                // Go ahead and clear it out
                console.log("Got a 401 from the server, triggering logout.");
                await sessionService.logout();
            }
            if (error.response && error.response.status === 403) {
                console.log("Got a 403 from the server, going to access denied page");
                router.push("/access-denied");
            }
            // No matter what, pass the error through for the component
            return Promise.reject(error);
        }
    );

    console.log("Starting Scorecard Service");
    const scoreService = new ScoreService();
    Vue.prototype.$scoreService = scoreService;

    console.log("Starting Incident Service");
    const incidentService = new IncidentService();
    Vue.prototype.$incidentService = incidentService;

    console.log("Starting Supplier Service");
    const supplierService = new SupplierService();
    Vue.prototype.$supplierService = supplierService;

    console.log("Starting Comment Service");
    const commentService = new CommentService();
    Vue.prototype.$commentService = commentService;

    console.log("Starting Action Service");
    const actionService = new ActionService();
    Vue.prototype.$actionService = actionService;

    console.log("Starting Timeline Service");
    const timelineService = new TimelineService();
    Vue.prototype.$timelineService = timelineService;

    console.log("Starting Document Upload Service");
    const documentUploadService = new DocumentUploadService();
    Vue.prototype.$documentUploadService = documentUploadService;

    console.log("Starting Document Download Service");
    const documentDownloadService = new DocumentDownloadService();
    Vue.prototype.$documentDownloadService = documentDownloadService;

    console.log("Starting Platform Document Upload Service");
    const platformDocumentUploadService = new PlatformDocumentUploadService();
    Vue.prototype.$platformDocumentUploadService = platformDocumentUploadService;

    console.log("Starting Note Service");
    const noteService = new NoteService();
    Vue.prototype.$noteService = noteService;

    console.log("Starting Read State Service");
    const readStateService = new ReadStateService();
    Vue.prototype.$readStateService = readStateService;

    console.log("Starting Supplier Event Service");
    const supplierEventService = new SupplierEventService();
    Vue.prototype.$supplierEventService = supplierEventService;

    console.log("Starting Supplier Product Service");
    const supplierProductService = new SupplierProductService();
    Vue.prototype.$supplierProductService = supplierProductService;

    console.log("Starting Platform User Info Service");
    const platformUserInfoService = new PlatformUserInfoService(pinia);
    Vue.prototype.$platformUserInfoService = platformUserInfoService;

    if (await sessionService.isUserLoggedIn()) {
        await platformUserInfoService.loadAuthorizationDataFromServer();
    }

    console.log("Starting Score Authorization Service");
    const scoreAuthorizationService = new ScoreAuthorizationService();
    Vue.prototype.$scoreAuthorizationService = scoreAuthorizationService;

    console.log("Starting Incident Authorization Service");
    const incidentAuthorizationService = new IncidentAuthorizationService();
    Vue.prototype.$incidentAuthorizationService = incidentAuthorizationService;

    console.log("Starting Action Authorization Service");
    const actionAuthorizationService = new ActionAuthorizationService();
    Vue.prototype.$actionAuthorizationService = actionAuthorizationService;

    console.log("Starting Supplier Event Authorization Service");
    const supplierEventAuthorizationService = new SupplierEventAuthorizationService();
    Vue.prototype.$supplierEventAuthorizationService = supplierEventAuthorizationService;

    const qRevPortalBaseURL = new URL(Vue.prototype.$configuration.baseAppURLs.qRevPortalBaseURL).host;
    const supplierPortalBaseURL = new URL(Vue.prototype.$configuration.baseAppURLs.supplierPortalBaseURL).host;
    const cqmsBaseURLs = [qRevPortalBaseURL, supplierPortalBaseURL];

    const userStore = useUserStore(pinia);
    // Set the referrer
    const currentUser = userStore.getUser();
    if (userStore.referrer == "") {
        // Check the referrer for this document
        if (document.referrer) {
            const referrerURL = new URL(document.referrer);
            const referrerHost = referrerURL.host;
            if (cqmsBaseURLs.includes(referrerHost)) {
                // We came from a C-QMS portal (Supplier or Internal);
                // The "Return to C-QMS" button should take us back there
                userStore.storeReferrer(referrerHost);
            } else {
                if (currentUser?.type == UserType.Internal) {
                    userStore.storeReferrer(qRevPortalBaseURL);
                } else {
                    userStore.storeReferrer(supplierPortalBaseURL);
                }
            }
        } else {
            if (currentUser?.type == UserType.Internal) {
                userStore.storeReferrer(qRevPortalBaseURL);
            } else {
                userStore.storeReferrer(supplierPortalBaseURL);
            }
        }
    }

    // Vue mounting is way down here so we can ensure a user is logged in first
    new Vue({
        router,
        vuetify,
        render: h => h(App),
        pinia,
    }).$mount("#app");
})();
